import type { BasicQuestion } from "@repo/app-types/client/client";
import type { Project } from "@repo/app-types/entities/project";
import {
  getProjectAndJobTypeProperty,
  JobTypeProperty,
} from "@repo/app-types/entities/project";
import type { ComplexQuestionInsert } from "@repo/app-types/entities/question";
import { getIsTrainingProject } from "@repo/app-types/helpers/utilities";
import { DropdownMenu } from "@repo/ui/Dropdown/dropdownMenu";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { IconDotsVertical } from "@tabler/icons-react";
import copy from "copy-to-clipboard";
import { nanoid } from "nanoid";
import { type FC, useState } from "react";
import { Button, MenuTrigger } from "react-aria-components";
import { useNavigate } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import { selectPhoneNumberAndExtensionForProject } from "../phoneNumbers/phoneNumbersSlice.js";
import { createProject } from "./projectsSlice.js";

interface ProjectMoreMenuProps {
  onClickPause?: () => void;
  onClickDeleteProject: () => void;
  onClickCopyInterviewLink?: () => void;
  onClickViewDialInstructions?: () => void;
  onClickCsvImport?: () => void;
  onClickCsvExport: () => void;
  onClickRegenerateInterviewLink: () => void;
  project: Project;
}

interface MenuItem {
  label: string;
  onClick: () => void;
}

export const ProjectMoreMenu: FC<ProjectMoreMenuProps> = ({
  onClickPause,
  onClickDeleteProject,
  onClickCopyInterviewLink,
  onClickViewDialInstructions,
  onClickCsvImport,
  onClickCsvExport,
  project,
  onClickRegenerateInterviewLink,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const phoneNumberInfo = useAppSelector(
    selectPhoneNumberAndExtensionForProject(project.id),
  );

  const isTrainingProject = getIsTrainingProject(project);
  const linkObjectLower = isTrainingProject ? "learning journey" : "interview";

  const [isDuplicateProjectModalOpen, setIsDuplicateProjectModalOpen] =
    useState<boolean>(false);

  const onConfirmDuplicateProject = async () => {
    try {
      await dispatch(
        createProject({
          name: `Copy of ${project.name}`,
          incentive_quantity: project.incentive_quantity || 0,
          questions: getProjectAndJobTypeProperty(
            project,
            JobTypeProperty.Questions,
          ).map((q) => {
            // Remove id and project_id so that server creates new questions
            const newQuestion: BasicQuestion | ComplexQuestionInsert = {
              ...q,
            };
            if ("id" in newQuestion) {
              delete newQuestion.id;
            }
            if ("project_id" in newQuestion) {
              delete newQuestion.project_id;
            }
            return newQuestion;
          }),
          short_id: nanoid(),
          settings: {
            ...project.settings,
            prompt_context: getProjectAndJobTypeProperty(
              project,
              JobTypeProperty.PromptContext,
            ),
          },
          mode: project.mode,
          phone_number_id: phoneNumberInfo?.phoneNumber.id,
          owner_teammate_ids: project.owner_teammate_ids,
          job_type_id: project.job_type?.id || undefined,
          // Training projects always duplicate the job type since sharing job types doesn't make sense for learning journeys.
          duplicate_job_type: isTrainingProject ? true : undefined,
        }),
      ).unwrap();

      showNotification(dispatch, {
        id: `project-duplicated-${new Date().getTime()}`,
        primaryMessage: `Project duplicated`,
        type: NotificationType.SUCCESS,
      });

      navigate(
        isTrainingProject ? appPaths.trainings.build() : appPaths.home.build(),
      );
    } catch (err) {
      console.error("error duplicating project", err);
    }
  };

  const menuItems: MenuItem[] = [
    { label: "Pause", onClick: onClickPause },
    {
      label: `Copy ${linkObjectLower} link`,
      onClick: onClickCopyInterviewLink,
    },
    {
      label: "View dialing instructions",
      onClick: onClickViewDialInstructions,
    },
    {
      label: "Copy project ID",
      onClick: () => {
        copy(project.id);
        showNotification(dispatch, {
          id: `project-${project.id}-id-copied-${new Date().getTime()}`,
          primaryMessage: `Project ID copied - use it to reference this project in the API`,
          type: NotificationType.SUCCESS,
        });
      },
    },
    {
      label: "Duplicate project",
      onClick: () => {
        setIsDuplicateProjectModalOpen(true);
      },
    },
    { label: "Import participants from CSV", onClick: onClickCsvImport },
    { label: "CSV export", onClick: onClickCsvExport },
    {
      label: `Refresh ${linkObjectLower} link`,
      onClick: onClickRegenerateInterviewLink,
    },
    { label: "Delete project", onClick: onClickDeleteProject },
  ].filter((item): item is MenuItem => item.onClick !== undefined);

  return (
    <>
      <div className="relative ml-3">
        <MenuTrigger>
          <Button className="relative flex items-center rounded-md bg-white p-1.5 text-foreground shadow-xs ring-1 ring-border-strong ring-inset hover:bg-gray-50 disabled:bg-disabled disabled:text-muted-foreground disabled:shadow-none">
            <span className="absolute -inset-1.5" />
            <span className="sr-only">Open user menu</span>
            <IconDotsVertical className="h-6 w-6" />
          </Button>
          <DropdownMenu items={menuItems} />
        </MenuTrigger>
      </div>
      <SimpleModal
        isOpen={isDuplicateProjectModalOpen}
        variant={SimpleModalVariant.Standard}
        title="Duplicate project"
        subtitle={
          isTrainingProject
            ? "A new project will be created with a copy of the learning journey and settings."
            : "A new project will be created with the same questions and settings as this project."
        }
        confirmButtonText="Continue"
        onCancel={() => {
          setIsDuplicateProjectModalOpen(false);
        }}
        onConfirm={onConfirmDuplicateProject}
      />
    </>
  );
};
